import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { userRequest } from '../api/requestMethods';
import { stashCardAuthContext } from "../utils/cardPayment";
import { withFreightCheckoutConsent } from "./freightCheckout";
import { isScheduleRejectionCode } from "../utils/deliverySchedule";
import { isUnsellableLineRefusal } from "../utils/priceOnRequest";
import { branchRefusalFields, branchRefusalOf } from "../utils/branchAvailability";

const loadOrderFromLocalStorage = () => {
    const savedOrder = localStorage.getItem('brandOrder');
    return savedOrder ? JSON.parse(savedOrder) : '';
};

const saveOrderToLocalStorage = (order) => {
    localStorage.setItem('brandOrder', JSON.stringify(order));
};

const initialState = {
    order: loadOrderFromLocalStorage(),
    orderCreationStatus: '',
    paymentCreationStatus: 'idle',
    error: null,
};

// MTN Mobile Money
export const payMoMoCheckout = createAsyncThunk(
    'brandOrder/payMoMoCheckout',
    async (data, { rejectWithValue }) => {
        const { client_id } = data;
        try {
            const response = await userRequest.post(`/momo/${client_id}`, data);
            return response.data;
        } catch (error) {
            console.log(error.response?.data?.message || error.response?.data?.msg || error.message);
            return rejectWithValue(error);
        }
    }
);

// Airtel Money
export const payAirtelMoneyCheckout = createAsyncThunk(
    'brandOrder/payAirtelCheckout',
    async (data, { rejectWithValue }) => {
        const { client_id } = data;
        try {
            const response = await userRequest.post(
                `/fw/flutterAirtelPayment/${client_id}`,
                data
            );
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.response?.data?.msg || error.message, {
                autoClose: false,
            });
            return rejectWithValue(error.response?.data?.message || error.response?.data?.msg || error.message);
        }
    }
);

// Payment with Credit Card
export const payCardCheckout = createAsyncThunk(
    'brandOrder/payCardCheckout',
    async (data, { rejectWithValue }) => {
        const { client_id } = data;
        // Stash sanitized context (last4/brand/amount) for the /pay/authorize page —
        // screens navigate there via window.location, so redux state does not survive.
        stashCardAuthContext(data?.cardDetails, data?.totalPrice);
        try {
            const response = await userRequest.post(
                `/fw/flutterCardPayment/${client_id}`,
                data,
                { withCredentials: true }
            );
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message, {
                autoClose: false,
            });
            return rejectWithValue(error.message || 'Unknown Error Occured!');
        }
    }
);

// Card payment PIN verification
export const submitCardPin_Otp = createAsyncThunk(
    'brandOrder/submitCardPin_Otp',
    async (data, { rejectWithValue }) => {
        const { client_id } = data;
        try {
            const response = await userRequest.post(
                `/fw/flutterSubmitCardPin_Otp/${client_id}`,
                data,
                { withCredentials: true }
            );
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message, {
                autoClose: false,
            });
            return rejectWithValue(error.message || 'Unknown Error Occured!');
        }
    }
);

export const createBrandOrder = createAsyncThunk(
    'brandOrder/createBrandOrder',
    async (data, { rejectWithValue, getState }) => {
        try {
            const res = await userRequest.post('/mobile', withFreightCheckoutConsent(data, getState(), "WEB"));
            toast.success('Order created successfully!');
            return res.data;
        } catch (err) {
            console.log(err, "Brand order creation error");
            // For HTTP 422 (OUT_OF_DELIVERY_RANGE / VENDOR_COORDS_MISSING) the
            // backend message explains the delivery-range problem; surface it.
            const status = err.response?.status;
            const message =
                err.response?.data?.message ||
                err.message ||
                'Failed to create order. Please try again.';
            // A delivery-time refusal opens the checkout's time picker dialog
            // instead (useCheckoutDeliverySchedule); a toast would double it.
            //
            // A PRICE_ON_REQUEST / PRICE_UNSET refusal is the same shape of
            // problem: a bare toast names a line the customer then has to go and
            // find, so the checkout screen opens a dialog that can take those
            // exact lines out of the cart or send them to the product to request
            // a quote. Toasting here as well would say it twice, in the weaker
            // of the two places.
            // A branch refusal (409 BRANCH_LINES_CHANGED, 4 Oct 2026) opens the
            // cart review from its `lines` (useBranchCartCheck) instead.
            const unsellableLine = isUnsellableLineRefusal(err);
            const branchRefusal = Boolean(branchRefusalOf(err.response?.data));
            if (
                !isScheduleRejectionCode(err.response?.data?.code) &&
                !unsellableLine &&
                !branchRefusal
            ) {
                toast.error(message, {
                    autoClose: false,
                });
            }
            // Reject with a serializable payload so the place-order screen can
            // detect the failure (and 422) and stop the flow before payment.
            // `violations` carries the server's per-product verdicts
            // ({ code, productId, name, message }); without it the screen can
            // only guess which line was refused, and dropping it is what forced
            // every earlier caller into a generic message.
            return rejectWithValue({
                status,
                code: err.response?.data?.code,
                message,
                violations: Array.isArray(err.response?.data?.violations)
                    ? err.response.data.violations
                    : undefined,
                ...branchRefusalFields(err.response?.data),
            });
        }
    }
);

export const fetchBrandOrders = createAsyncThunk(
    'brandOrder/fetchBrandOrders',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await userRequest.get(`/mobile/${userId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching brand orders:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

const brandOrderSlice = createSlice({
    name: 'brandOrder',
    initialState,
    reducers: {
        resetOrderCreation: (state) => {
            state.order = '';
            state.orderCreationStatus = '';
            state.paymentCreationStatus = '';
            state.error = null;
            localStorage.removeItem('brandOrder');
        },
        resetBrandOrder: (state) => {
            return initialState;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(createBrandOrder.pending, (state) => {
                state.orderCreationStatus = 'pending';
            })
            .addCase(createBrandOrder.fulfilled, (state, action) => {
                state.orderCreationStatus = 'fulfilled';
                state.order = action.payload;
                saveOrderToLocalStorage(action.payload);
            })
            .addCase(createBrandOrder.rejected, (state, action) => {
                state.orderCreationStatus = 'rejected';
                state.error = action.payload;
            })
            .addCase(payMoMoCheckout.pending, (state) => {
                state.paymentCreationStatus = 'pending';
            })
            .addCase(payMoMoCheckout.fulfilled, (state) => {
                state.paymentCreationStatus = 'fulfilled';
            })
            .addCase(payMoMoCheckout.rejected, (state, action) => {
                state.paymentCreationStatus = 'rejected';
                state.error = action.payload;
            })
            .addCase(payCardCheckout.pending, (state) => {
                state.paymentCreationStatus = 'pending';
            })
            .addCase(payCardCheckout.fulfilled, (state, action) => {
                state.paymentCreationStatus = 'fulfilled';
                state.order = action.payload;
                saveOrderToLocalStorage(action.payload);
            })
            .addCase(payCardCheckout.rejected, (state, action) => {
                state.paymentCreationStatus = 'rejected';
                state.error = action.payload;
            })
            .addCase(payAirtelMoneyCheckout.pending, (state) => {
                state.paymentCreationStatus = 'processing payment';
            })
            .addCase(payAirtelMoneyCheckout.fulfilled, (state, action) => {
                state.paymentCreationStatus = 'fulfilled';
                state.order = action.payload;
                saveOrderToLocalStorage(action.payload);
            })
            .addCase(payAirtelMoneyCheckout.rejected, (state, action) => {
                state.paymentCreationStatus = 'rejected';
                state.error = action.payload;
            })
            .addCase(submitCardPin_Otp.pending, (state) => {
                state.paymentCreationStatus = 'pending';
            })
            .addCase(submitCardPin_Otp.fulfilled, (state, action) => {
                state.paymentCreationStatus = 'fulfilled';
                state.order = action.payload;
                saveOrderToLocalStorage(action.payload);
            })
            .addCase(submitCardPin_Otp.rejected, (state, action) => {
                state.paymentCreationStatus = 'rejected';
                state.error = action.payload;
            });
    },
});

export const { resetOrderCreation, resetBrandOrder } = brandOrderSlice.actions;
export default brandOrderSlice.reducer;
